import type { QueryClient, QueryKey } from '@tanstack/react-query'
import * as React from 'react'

/**
 * The SSE client (scope §9 Realtime). `GET /api/v1/stream` sends `event: invalidate` with
 * `data: {"event_id": n, "kind": "...", "keys": [["queue"], ["case", "<id>"], ...]}`. Each key becomes a
 * TanStack Query prefix invalidation, so the screen showing that data refetches and nothing else does.
 * The stream carries keys only, never content (the events table is the truth).
 *
 * Coalescing: a busy Desk day streams several events a second, and nearly every one names ["queue"] and ["home"].
 * Each query key is invalidated at most once per `coalesceMs` (default 4 s): the first event fires at once (leading
 * edge), later ones inside the window collapse into a single trailing invalidation at the end of the window, so the
 * last change is never lost and the API sees one refetch per screen per window instead of one per event.
 *
 * Resume: the browser's own EventSource reconnect sends `Last-Event-ID`, and the API replays the gap. When
 * the stream closes for good (401, 5xx) we open a new EventSource after a backoff; a new instance cannot
 * send `Last-Event-ID`, so on that reopen every active query is invalidated instead. Nothing is missed
 * either way.
 */

export const STREAM_URL = '/api/v1/stream'

export type StreamState = 'connecting' | 'open' | 'retrying' | 'closed'

export interface InvalidateMessage {
  event_id?: number
  kind?: string
  keys: QueryKey[]
}

/** Accepts only arrays of string/number keys; anything else is dropped (a bad event must not crash the shell). */
export function parseInvalidate(data: string): InvalidateMessage | null {
  let body: unknown
  try {
    body = JSON.parse(data)
  } catch {
    return null
  }
  if (!body || typeof body !== 'object') return null
  const raw = (body as { keys?: unknown }).keys
  if (!Array.isArray(raw)) return null
  const keys = raw.filter(
    (k): k is (string | number)[] =>
      Array.isArray(k) && k.length > 0 && k.length <= 6 && k.every((p) => typeof p === 'string' || typeof p === 'number'),
  )
  const { event_id, kind } = body as { event_id?: unknown; kind?: unknown }
  return {
    keys,
    event_id: typeof event_id === 'number' ? event_id : undefined,
    kind: typeof kind === 'string' ? kind : undefined,
  }
}

export interface StreamOptions {
  url?: string
  /** Injected in tests. */
  EventSourceImpl?: typeof EventSource
  /** Backoff after a closed stream: 1 s doubling to 30 s. */
  minDelayMs?: number
  maxDelayMs?: number
  /** At most one invalidation per query key per window (leading + trailing edge). 0 disables coalescing. */
  coalesceMs?: number
  onState?: (state: StreamState) => void
  /** Called when the stream closed for good; the shell re-checks the session (a 401 closes the stream). */
  onClosed?: () => void
}

export const COALESCE_MS = 4000

/**
 * Key aliases (#226): the server names a case by its uuid (`["case", "<uuid>"]`), while a screen may cache it under
 * another key (the case view keys on the human number, `["case", "C-00387"]`). A mounted screen registers the alias
 * so a server event for that case refreshes what is on screen. Exact-match on the incoming key.
 */
const aliases = new Map<string, Map<number, QueryKey>>()
let aliasSeq = 0

export function registerKeyAlias(from: QueryKey, to: QueryKey): () => void {
  const id = JSON.stringify(from)
  const seq = ++aliasSeq
  const set = aliases.get(id) ?? new Map<number, QueryKey>()
  set.set(seq, to)
  aliases.set(id, set)
  return () => {
    const cur = aliases.get(id)
    cur?.delete(seq)
    if (cur && cur.size === 0) aliases.delete(id)
  }
}

/** The key itself plus every alias registered for it. */
export function expandKey(queryKey: QueryKey): QueryKey[] {
  const extra = aliases.get(JSON.stringify(queryKey))
  return extra ? [queryKey, ...extra.values()] : [queryKey]
}

/** Registers `from -> to` aliases while the calling component is mounted. */
export function useKeyAliases(pairs: [QueryKey, QueryKey][]): void {
  const sig = JSON.stringify(pairs)
  React.useEffect(() => {
    const parsed = JSON.parse(sig) as [QueryKey, QueryKey][]
    const offs = parsed.map(([from, to]) => registerKeyAlias(from, to))
    return () => {
      for (const off of offs) off()
    }
  }, [sig])
}

/**
 * Per-key throttle with a trailing edge: `push(key)` invalidates now when the key has not fired within `windowMs`,
 * else schedules exactly one invalidation for the end of the window. `stop()` drops pending timers.
 */
export function createCoalescer(fire: (queryKey: QueryKey) => void, windowMs: number) {
  const last = new Map<string, number>()
  const pending = new Map<string, ReturnType<typeof setTimeout>>()
  return {
    push(queryKey: QueryKey) {
      if (windowMs <= 0) {
        fire(queryKey)
        return
      }
      const id = JSON.stringify(queryKey)
      if (pending.has(id)) return
      const now = Date.now()
      const wait = (last.get(id) ?? Number.NEGATIVE_INFINITY) + windowMs - now
      if (wait <= 0) {
        last.set(id, now)
        fire(queryKey)
        return
      }
      pending.set(
        id,
        setTimeout(() => {
          pending.delete(id)
          last.set(id, Date.now())
          fire(queryKey)
        }, wait),
      )
    },
    stop() {
      for (const t of pending.values()) clearTimeout(t)
      pending.clear()
    },
  }
}

/** Connects and returns a disconnect function. */
export function connectInvalidationStream(queryClient: QueryClient, opts: StreamOptions = {}): () => void {
  const ES = opts.EventSourceImpl ?? globalThis.EventSource
  if (!ES) {
    opts.onState?.('closed')
    return () => {}
  }
  const url = opts.url ?? STREAM_URL
  const min = opts.minDelayMs ?? 1000
  const max = opts.maxDelayMs ?? 30_000
  let delay = min
  let source: EventSource | null = null
  let timer: ReturnType<typeof setTimeout> | null = null
  let stopped = false
  let reopened = false
  const coalescer = createCoalescer((queryKey) => void queryClient.invalidateQueries({ queryKey }), opts.coalesceMs ?? COALESCE_MS)

  const onInvalidate = (ev: MessageEvent) => {
    const msg = parseInvalidate(String(ev.data))
    if (!msg) return
    for (const queryKey of msg.keys) for (const key of expandKey(queryKey)) coalescer.push(key)
    // A changed case can change its own close-out blockers. The server already emits ['queue'] for mutations that
    // can move a lane; adding another broad client-side queue invalidation only repeated that work.
    for (const key of msg.keys) {
      if (key[0] === 'case' && typeof key[1] === 'string') {
        coalescer.push(['closeout', 'case', key[1]])
        coalescer.push(['closeout', 'staff-reminders', key[1]])
      }
    }
  }

  const open = () => {
    if (stopped) return
    opts.onState?.(reopened ? 'retrying' : 'connecting')
    const es = new ES(url, { withCredentials: true })
    source = es
    es.addEventListener('invalidate', onInvalidate as EventListener)
    es.onopen = () => {
      delay = min
      opts.onState?.('open')
      // A fresh EventSource could not send Last-Event-ID. Mark every cached query stale: active ones
      // refetch now and inactive ones refetch when opened, so a missed update can never look fresh later.
      if (reopened) void queryClient.invalidateQueries()
    }
    es.onerror = () => {
      // CONNECTING: the browser is retrying itself (with Last-Event-ID). CLOSED: it gave up.
      if (es.readyState !== 2) {
        opts.onState?.('retrying')
        return
      }
      es.close()
      source = null
      opts.onState?.('closed')
      opts.onClosed?.()
      if (stopped) return
      reopened = true
      timer = setTimeout(open, delay)
      delay = Math.min(delay * 2, max)
    }
  }

  open()
  return () => {
    stopped = true
    if (timer) clearTimeout(timer)
    coalescer.stop()
    source?.close()
    source = null
  }
}

/** Mounts the stream for the signed-in shell. `enabled` is false on /signin and while signed out. */
export function useInvalidationStream(queryClient: QueryClient, enabled: boolean, opts: StreamOptions = {}): StreamState {
  const [state, setState] = React.useState<StreamState>('closed')
  const onClosed = opts.onClosed
  React.useEffect(() => {
    if (!enabled) {
      setState('closed')
      return
    }
    return connectInvalidationStream(queryClient, { onState: setState, onClosed })
  }, [queryClient, enabled, onClosed])
  return state
}
